import React, { useState } from "react";

import { css, cx } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { LGCImportInfoDialog } from "@/uiKit/organisms/PatientSync/LGCImportInfoDialog.tsx";
import { downloadFileFromBlob } from "@/utils/fileUtils.ts";

export type OngoingImportSidebarProps = {
  completedInclusionIds: string[];
  className?: string;
};

export const OngoingImportSidebar: React.FC<OngoingImportSidebarProps> = ({ completedInclusionIds, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const [lgcDialogOpen, setLgcDialogOpen] = useState(false);

  const proposeTelemonitoring = Queries.patientSync.useBulkSubmitGroupedInclusionDrafts();
  const exportXls = Queries.patientSync.useExportGroupedInclusionDraftsXls();

  return (
    <div className={cx(styles.container, className)}>
      <Card>
        <CardHeader title={t("patientSync.ongoingImport.sidebar.selfTitle")} />
        <CardContent>
          <Typography variant="body">{t("patientSync.ongoingImport.sidebar.selfBody")}</Typography>
        </CardContent>
        <CardFooter position="start">
          <div className={styles.selfActions}>
            <Button
              variant="contained"
              data-action="sync.ongoing-import.submit"
              disabled={completedInclusionIds.length === 0}
              onClick={() =>
                proposeTelemonitoring.mutate(completedInclusionIds, {
                  onSuccess: () => {
                    TextToast.success(
                      t("patientSync.groupedInclusion.proposeTelemonitoringSuccess", {
                        count: completedInclusionIds.length,
                      }),
                    );
                  },
                  onError: () => {
                    TextToast.error(t("errors.unknownError"));
                  },
                })
              }
            >
              {t("patientSync.groupedInclusion.proposeTelemonitoring", { count: completedInclusionIds.length })}
            </Button>
          </div>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={t("patientSync.ongoingImport.sidebar.delegateTitle")} />
        <CardContent>
          <Typography variant="body">{t("patientSync.ongoingImport.sidebar.delegateBody")}</Typography>
        </CardContent>
        <CardFooter position="start">
          <Button
            variant="outlined"
            data-action="sync.ongoing-import.download"
            startIcon={<Icons.globe className={styles.icon} />}
            onClick={() =>
              exportXls.mutate(
                {},
                {
                  onSuccess: blob => {
                    downloadFileFromBlob(
                      blob,
                      `imports_to_complete_${DateTime.now().toLocaleString(DateTime.DATE_SHORT)}.xlsx`,
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    );
                    TextToast.success(t("patientSync.ongoingImport.sidebar.delegateSuccess"));
                  },
                  onError: () => {
                    TextToast.error(t("errors.unknownError"));
                  },
                },
              )
            }
          >
            {t("patientSync.ongoingImport.sidebar.delegateButton")}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={t("patientSync.ongoingImport.sidebar.lgcTitle")} />
        <CardContent>
          <Typography variant="body">{t("patientSync.ongoingImport.sidebar.lgcBody")}</Typography>
        </CardContent>
        <CardFooter position="start">
          <Button variant="outlined" data-action="sync.lgc.open-info" onClick={() => setLgcDialogOpen(true)}>
            {t("patientSync.ongoingImport.sidebar.lgcButton")}
          </Button>
        </CardFooter>
      </Card>

      <LGCImportInfoDialog open={lgcDialogOpen} onClose={() => setLgcDialogOpen(false)} />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  selfActions: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(4),
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
